/**
 * "Desk View" in the sidebar — the check-in screen, where a receptionist lives.
 *
 * Three regions: the inbox on the left, the document in the middle, the form
 * on the right. The inbox folds to a rail, so the document can have the
 * width - reading a printed card is the job, choosing the next one is not. The state of the right-hand pane follows the work:
 *
 *   nothing selected  ->  empty state explaining what goes here
 *   capture selected  ->  extraction review, correct then push
 *   session live      ->  mirror of the guest's screen, with chat
 *   guest submitted   ->  approval panel with the stamped preview
 *
 * Everything asynchronous shows its state. The legacy screen's frozen "load
 * document" button is the specific anti-pattern this replaces.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileUp, Inbox } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { getSocket, joinSession, leaveSession, noteSeq, on } from '@/lib/socket'
import type { CaptureJob, SigningSession } from '@/lib/types'
import { Button } from '@/components/ui/primitives'
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { ConfirmDestructive } from '@/components/domain/ConfirmDestructive'
import { InboxPanel, InboxRail } from '@/features/workspace/InboxPanel'
import { ExtractionReview } from '@/features/workspace/ExtractionReview'
import { SessionMirror } from '@/features/workspace/SessionMirror'
import { UploadDialog } from '@/features/workspace/UploadDialog'

const TERMINAL = ['completed', 'cancelled', 'expired']

function isTerminal(status: string) {
  return TERMINAL.includes(status)
}

const INBOX_KEY = 'ereg.desk.inbox'

/** Whether the inbox is unfolded, remembered per browser. Open by default. */
function useInboxOpen() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(INBOX_KEY) !== 'closed'
    } catch {
      return true
    }
  })
  const set = useCallback((next: boolean) => {
    setOpen(next)
    try {
      localStorage.setItem(INBOX_KEY, next ? 'open' : 'closed')
    } catch {
      // Private window or blocked storage: the choice lasts until reload.
    }
  }, [])
  return [open, set] as const
}

export function WorkspacePage() {
  const { session: auth } = useAuth()
  const queryClient = useQueryClient()
  const toast = useToast()

  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [reviewDirty, setReviewDirty] = useState(false)
  const [adoptingId, setAdoptingId] = useState<string | null>(null)
  const [confirmClear, setConfirmClear] = useState<'failed' | 'all' | null>(null)
  const [inboxOpen, setInboxOpen] = useInboxOpen()

  const desk = auth?.desk ?? null

  const inbox = useQuery({
    queryKey: ['capture', 'inbox'],
    queryFn: () => api.get<CaptureJob[]>('/capture/inbox'),
    // A short poll as a safety net under the socket: if the connection is
    // flapping the receptionist still sees new prints arrive.
    refetchInterval: 20_000,
    enabled: Boolean(desk),
  })

  const liveSession = useQuery({
    queryKey: ['sessions', 'active'],
    queryFn: () => api.get<SigningSession | null>('/sessions/active'),
    refetchInterval: 30_000,
    enabled: Boolean(desk),
  })

  const selectedJob = useQuery({
    queryKey: ['capture', 'job', selectedJobId],
    queryFn: () => api.get<CaptureJob>(`/capture/jobs/${selectedJobId}`),
    enabled: Boolean(selectedJobId),
  })

  const refreshInbox = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['capture'] })
  }, [queryClient])

  // "That is my PC." The one click that replaces choosing between five
  // virtual printers: it puts this print on my desk and binds the machine
  // that sent it, so the next one arrives here with no click at all.
  const adopt = useMutation({
    mutationFn: (jobId: string) => api.post<CaptureJob>(`/capture/jobs/${jobId}/assign`),
    onMutate: (jobId: string) => setAdoptingId(jobId),
    onSuccess: (job: CaptureJob) => {
      refreshInbox()
      setSelectedJobId(job.id)
      toast.success(
        'Sent to your desk',
        `${desk?.name ?? 'This desk'} now receives prints from that PC.`,
      )
    },
    onError: (error: Error) => toast.error('Could not take that document', error.message),
    onSettled: () => setAdoptingId(null),
  })

  // Two counts, because the two clears are different acts. Failures
  // accumulate — nothing consumes them, and each one sits in front of the
  // document a guest is waiting for; one binary being wrong for an afternoon
  // buries the inbox. `clearable` is everything at this desk, unclaimed prints
  // from other people's PCs excluded, matching what the server will remove.
  const { failedIds, clearableIds } = useMemo(() => {
    const jobs = inbox.data ?? []
    return {
      failedIds: jobs.filter((job) => job.status === 'failed').map((job) => job.id),
      clearableIds: jobs.filter((job) => job.desk_id).map((job) => job.id),
    }
  }, [inbox.data])

  const clearInbox = useMutation({
    mutationFn: (scope: 'failed' | 'all') =>
      api.delete<{ cleared: number }>('/capture/inbox', { scope }),
    onSuccess: (result: { cleared: number }, scope: 'failed' | 'all') => {
      // The pane may be showing one of the rows that just went; leaving it
      // selected turns the review into a 404 the moment it refetches.
      const gone = scope === 'failed' ? failedIds : clearableIds
      if (selectedJobId && gone.includes(selectedJobId)) {
        setSelectedJobId(null)
        setReviewDirty(false)
      }
      refreshInbox()
      const count = `${result.cleared} document${result.cleared === 1 ? '' : 's'}`
      toast.success(
        scope === 'failed' ? `Cleared ${count} that failed` : `Cleared ${count}`,
        scope === 'failed'
          ? 'Anything still being read, waiting for review or ready to send was left alone.'
          : 'Signed documents are in the archive, and an unclaimed print from another PC stays where it is.',
      )
    },
    onError: (error: Error) => toast.error('Could not clear the inbox', error.message),
    onSettled: () => setConfirmClear(null),
  })

  const refreshSession = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['sessions'] })
  }, [queryClient])

  // Live updates. Everything here is additive to the polling above, so a
  // dropped socket degrades to "slightly slower" rather than "broken".
  useEffect(() => {
    if (!getSocket()) return
    let announced: string | null = null
    const unsubscribers = [
      // The print has landed, before the worker has even opened it. This is
      // what makes "print, and it is on screen" true: everything else about a
      // capture is emitted by the worker, several seconds later, and until
      // this event existed the only thing telling the desk a print had
      // arrived at all was a twenty-second poll.
      on<{
        job_id: string
        desk_id: string | null
        workstation: string | null
      }>('capture.received', (payload) => {
        refreshInbox()
        if (payload.desk_id) toast.info('A document arrived', 'Reading it now.')
      }),
      // Somebody claimed an unassigned print - possibly not us. Either way the
      // card should stop offering itself to everyone else in the room.
      on<{ job_id: string; desk_id: string }>('capture.assigned', () => refreshInbox()),
      on<{ job_id: string; percent: number }>('capture.progress', () => refreshInbox()),
      on<{ job_id: string }>('capture.ready', (payload) => {
        refreshInbox()
        toast.success('A document is ready to review')
        setSelectedJobId((current) => current ?? payload.job_id)
      }),
      on<{ job_id: string; error: string }>('capture.failed', (payload) => {
        refreshInbox()
        toast.error('A document could not be read', payload.error)
      }),
      on<{ session_id: string; status: string; seq: number; actor?: string }>(
        'session.state_changed',
        (payload) => {
          refreshSession()
          // The desk hears a state change in both its own room and the
          // session's, so the same change arrives twice: say it once.
          const key = `${payload.session_id}:${payload.seq}`
          if (payload.status === 'cancelled' && payload.actor === 'guest' && announced !== key) {
            announced = key
            toast.info('The guest cancelled on the tablet', 'Send the document again when they are ready.')
          }
        },
      ),
      on('session.ready_for_approval', () => {
        refreshSession()
        toast.info('The guest has finished', 'Review and approve the document.')
      }),
      on<{ document_id: string }>('session.completed', () => {
        refreshSession()
        refreshInbox()
        setReviewDirty(false)
        setSelectedJobId(null)
        toast.success('Document saved', 'The guest copy is archived and shared.')
      }),
      on<{ error: string }>('session.failed', (payload) => {
        refreshSession()
        toast.error('The document could not be saved', payload.error)
      }),
    ]
    return () => unsubscribers.forEach((off) => off())
  }, [refreshInbox, refreshSession, toast])

  const active = liveSession.data ?? null
  const activeId = active && !isTerminal(active.status) ? active.id : null

  // Join the session's own room. `session.field_changed`, `checkbox_changed`,
  // `signature_updated` and `message` are emitted *only* there, and nothing on
  // the desk ever joined it — so the "live" mirror moved only when the
  // thirty-second poll happened to land, and the change pulse never fired at
  // all. The server guards membership, so joining our own desk's session is
  // safe.
  useEffect(() => {
    if (!activeId || !getSocket()) return
    void joinSession(activeId, 0).catch(() => undefined)
    return () => leaveSession(activeId)
  }, [activeId])

  useEffect(() => {
    if (activeId && active) noteSeq(activeId, active.seq)
  }, [activeId, active])

  // A live session takes the pane: the guest is holding the tablet and that is
  // the only thing that matters right now. Unless the receptionist is midway
  // through correcting a capture — a stale session from an earlier guest
  // surfacing on the poll used to snatch the pane and bin the typing.
  const pane = useMemo(() => {
    if (activeId && !(reviewDirty && selectedJobId)) return 'session' as const
    if (selectedJobId) return 'review' as const
    if (activeId) return 'session' as const
    return 'empty' as const
  }, [activeId, reviewDirty, selectedJobId])

  if (!desk) {
    return (
      <>
        <PageHeader
          title="Desk View"
          description="Capture a document, send it to the guest tablet, approve the signature."
        />
        <EmptyState
          title="Claim a desk to start"
          description="Documents arrive at a desk, and a tablet is bound to a desk. Pick yours from the chip in the top bar."
          icon={<Inbox className="h-6 w-6" />}
        />
      </>
    )
  }

  // From xl the page is exactly the viewport under the top bar (3.5rem) and
  // main's padding, which the shell tightens to 1.5rem here: the inbox, the
  // document and the field list each scroll inside their own box, so "Send to
  // tablet" never sits below the fold. Narrower than that the panes stack and
  // the page scrolls as normal.
  return (
    <div className="xl:flex xl:h-[calc(100dvh-5rem)] xl:flex-col">
      {/* From xl the title row goes: the sidebar already says where you are,
          the desk is on the chip in the top bar, and upload is on the inbox.
          It was 48px of height the page could not have. */}
      <h1 className="hidden xl:block xl:sr-only">Desk View</h1>
      <PageHeader
        className="items-center pb-3 xl:hidden"
        inline
        title="Desk View"
        description={`${desk.name}${active ? ` · session ${active.status.replace(/_/g, ' ')}` : ''}`}
        actions={
          <Button
            variant="secondary"
            icon={<FileUp className="h-4 w-4" />}
            onClick={() => setUploadOpen(true)}
          >
            Upload a PDF
          </Button>
        }
      />

      <div
        className={cn(
          'grid gap-3 xl:min-h-0 xl:flex-1 xl:grid-rows-[minmax(0,1fr)]',
          inboxOpen
            ? 'lg:grid-cols-[300px_minmax(0,1fr)] 2xl:grid-cols-[320px_minmax(0,1fr)]'
            : 'lg:grid-cols-[2.75rem_minmax(0,1fr)]',
        )}
      >
        <aside
          aria-label="Inbox"
          className="scroll-thin lg:sticky lg:top-20 lg:self-start xl:static xl:min-h-0 xl:self-stretch xl:overflow-y-auto xl:pe-1"
        >
          {!inboxOpen ? (
            <InboxRail
              jobs={inbox.data ?? []}
              onExpand={() => setInboxOpen(true)}
              onUpload={() => setUploadOpen(true)}
            />
          ) : inbox.isPending ? (
            <LoadingState rows={3} />
          ) : inbox.isError ? (
            <ErrorState error={inbox.error} onRetry={() => void inbox.refetch()} />
          ) : (
            <InboxPanel
              jobs={inbox.data ?? []}
              selectedId={selectedJobId}
              onSelect={setSelectedJobId}
              onAdopt={(jobId) => adopt.mutate(jobId)}
              adoptingId={adoptingId}
              onClear={(scope) => setConfirmClear(scope)}
              clearing={clearInbox.isPending ? clearInbox.variables : null}
              disabled={pane === 'session'}
              onCollapse={() => setInboxOpen(false)}
              onUpload={() => setUploadOpen(true)}
            />
          )}
        </aside>

        <section className="min-w-0 xl:flex xl:min-h-0 xl:flex-col">
          {pane === 'review' && activeId && (
            <div
              role="status"
              className="mb-3 flex shrink-0 flex-wrap items-center gap-3 rounded-[10px] border border-[var(--info)] bg-[var(--info-subtle)] px-3 py-2 text-[12px] text-[var(--info)]"
            >
              <span>A guest is signing at this desk. Your corrections here are saved.</span>
              <Button
                variant="secondary"
                size="sm"
                className="ms-auto"
                onClick={() => {
                  setReviewDirty(false)
                  setSelectedJobId(null)
                }}
              >
                Go to the live session
              </Button>
            </div>
          )}

          {pane === 'session' && active ? (
            <SessionMirror
              session={active}
              onChanged={refreshSession}
              onFinished={() => {
                setSelectedJobId(null)
                refreshSession()
                refreshInbox()
              }}
            />
          ) : pane === 'review' ? (
            selectedJob.isPending ? (
              <LoadingState rows={6} />
            ) : selectedJob.isError ? (
              <ErrorState error={selectedJob.error} onRetry={() => void selectedJob.refetch()} />
            ) : (
              // Keyed on the job. Without it React reuses the component across
              // a job switch, and the local edit state goes with it: one
              // guest's typed surname appeared in — and was saved onto —
              // the next guest's registration card.
              <ExtractionReview
                key={selectedJob.data!.id}
                job={selectedJob.data!}
                onDirtyChange={setReviewDirty}
                // Just let go of it. No refetch: nothing about the job
                // changed, and the inbox card is still the same card.
                onClose={() => {
                  setReviewDirty(false)
                  setSelectedJobId(null)
                }}
                onDeleted={() => {
                  setReviewDirty(false)
                  setSelectedJobId(null)
                  refreshInbox()
                }}
                onPushed={() => {
                  setReviewDirty(false)
                  setSelectedJobId(null)
                  refreshSession()
                  refreshInbox()
                }}
                onCorrected={() => void selectedJob.refetch()}
              />
            )
          ) : (
            <EmptyState
              title="No document loaded"
              description="Print a registration card from your PMS to the EREG printer and it appears here in about a second. You can also upload a PDF."
              icon={<Inbox className="h-6 w-6" />}
              action={
                <Button variant="primary" onClick={() => setUploadOpen(true)}>
                  Upload a PDF
                </Button>
              }
            />
          )}
        </section>
      </div>

      {/* Both scopes confirm. The failed one describes what it is throwing
          away because nobody wants it; the "all" one describes what it is
          throwing away because somebody might. */}
      <ConfirmDestructive
        open={confirmClear !== null}
        title={
          confirmClear === 'all'
            ? `Clear all ${clearableIds.length} document${clearableIds.length === 1 ? '' : 's'} from this desk?`
            : `Clear ${failedIds.length} failed document${failedIds.length === 1 ? '' : 's'}?`
        }
        description={
          confirmClear === 'all'
            ? 'This empties the inbox — including documents that are ready to send or waiting for review, and any still being read. None of them have reached a guest yet, but anything a guest still needs will have to be printed again. Documents already signed are in the archive and are not affected, and an unclaimed print from a PC nobody has adopted stays where it is.'
            : 'These could not be read, so none of them reached a guest. Their PDFs are removed. Everything still being read, waiting for review or ready to send stays where it is — and if one came from the PMS you can simply print it again.'
        }
        confirmLabel={confirmClear === 'all' ? 'Clear everything' : 'Clear them'}
        cancelLabel={
          (confirmClear === 'all' ? clearableIds.length : failedIds.length) === 1
            ? 'Keep it'
            : 'Keep them'
        }
        loading={clearInbox.isPending}
        onCancel={() => setConfirmClear(null)}
        onConfirm={() => clearInbox.mutate(confirmClear ?? 'failed')}
      />

      <UploadDialog
        open={uploadOpen}
        deskId={desk.id}
        onClose={() => setUploadOpen(false)}
        onUploaded={(jobId) => {
          setUploadOpen(false)
          setSelectedJobId(jobId)
          refreshInbox()
        }}
      />
    </div>
  )
}
